// Pure builders for the body FRAGMENT that each plain-text email layout stores.
//
// crm-api's mailer (campaign_email.html.erb) owns ALL the chrome — the CID brand
// logo header, the personalized greeting, and the tenant footer/unsubscribe — so
// these builders emit ONLY the message body. Emitting a *complete* HTML document
// here (its own <!DOCTYPE>, logo and footer) is exactly what caused the
// double-wrapped "two logos / two footers / empty gray bars" bug: the model
// strips the document tags off non-builder layouts, the mailer no longer
// recognized it as complete HTML, and wrapped it a second time. A fragment can't
// double-wrap.
//
// Kept out of the React components so they're unit-testable without importing
// MUI / TipTap.

export function buildSimpleTextFragment(content: string, signature: string): string {
  const body = (content ?? "").trim()
  const sig = (signature ?? "").trim()
  const signatureBlock = sig
    ? `<div style="margin-top:30px;padding-top:20px;border-top:1px solid #e5e7eb;">` +
      `<p style="margin:0 0 8px 0;font-weight:500;">Best regards,</p>` +
      `<p style="margin:0;">${sig}</p></div>`
    : ""
  return `<div>${body}</div>${signatureBlock}`
}

const SIGN_OFF = /^best regards,?$/i

const textOf = (el: Element | null | undefined) => (el?.textContent ?? "").replace(/\s+/g, " ").trim()

/**
 * The inverse of buildSimpleTextFragment, for re-opening a saved campaign.
 *
 * The editor used to load the SAVED email — body AND signature block — back in
 * as the body, then append a fresh signature on save. Every reopen added one
 * more "Best regards": the webinar thank-you of 2026-10-07 went to 1,431
 * people with three. This splits the stored fragment back into the body and
 * the signature name, and also strips sign-offs ("Best regards," + that name)
 * that already piled up at the end of the body, so a damaged campaign heals
 * the next time it is opened.
 *
 * `signature` is null when the stored email had no signature block (the
 * operator cleared it) — the caller keeps it empty rather than re-adding one.
 */
export function parseSimpleTextFragment(html: string): { body: string; signature: string | null } {
  const doc = new DOMParser().parseFromString(html ?? "", "text/html")
  const root = doc.body

  let signature: string | null = null
  const last = root.lastElementChild
  if (last && last.tagName === "DIV" && last !== root.firstElementChild) {
    const paragraphs = Array.from(last.querySelectorAll("p"))
    if (paragraphs.length >= 2 && SIGN_OFF.test(textOf(paragraphs[0]))) {
      signature = textOf(paragraphs[paragraphs.length - 1])
      last.remove()
    }
  }

  // The body is the one wrapper <div> buildSimpleTextFragment emits (older
  // campaigns may be bare paragraphs).
  const only = root.children.length === 1 ? root.firstElementChild : null
  const container = only && only.tagName === "DIV" && !only.getAttribute("style") ? only : root

  // Trailing empty paragraphs and piled-up sign-offs come off the body end.
  const isEmpty = (el: Element) => el.tagName === "P" && textOf(el) === "" && !el.querySelector("img")
  for (;;) {
    const tail = container.lastElementChild
    if (!tail) break
    if (isEmpty(tail)) { tail.remove(); continue }
    const before = tail.previousElementSibling
    if (
      signature !== null && before && tail.tagName === "P" && before.tagName === "P" &&
      SIGN_OFF.test(textOf(before)) && textOf(tail) === signature
    ) {
      tail.remove()
      before.remove()
      continue
    }
    if (signature !== null && tail.tagName === "P" && SIGN_OFF.test(textOf(tail))) { tail.remove(); continue }
    break
  }

  return { body: container.innerHTML.trim(), signature }
}

// CEO letter: the bare body only. crm-api prepends the personalized
// "Hi <first name>," greeting, so the fragment must NOT start with its own.
export function buildCeoLetterFragment(content: string): string {
  return `<div>${(content ?? "").trim()}</div>`
}
